<template>
  <ProgressModal
    v-if="isLoadingAction"
    title="Please wait..."
    :progressIndicator="progressIndicator"
    :processedItems="processedItems"
    :totalItems="campaignsSelected.length"
  />

  <div
    id="promotions-hub-view"
    :class="`content-wrapper animation-slide ${
      isMenuOpen ? 'menu-open' : 'menu-closed'
    }`"
  >
    <div class="content content-styles text-slate-800 ov-visible md:mx-[1rem]">
      <!-- Header -->
      <div
        class="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      >
        <div>
          <div class="flex items-center gap-2 mb-1.5">
            <span
              class="px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200"
            >
              <i class="fa-solid fa-sparkles mr-1 text-indigo-500"></i>
              Engagement Center
            </span>
          </div>
          <h1
            class="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900"
          >
            Promotions Hub
          </h1>
          <p class="text-xs text-slate-500 mt-1">
            Manage all player engagement campaigns, tournaments, races, and drop
            events
          </p>
        </div>

        <!-- Header Actions -->
        <div class="flex items-center gap-2.5 flex-wrap">
          <!-- Campaign Guide Button -->
          <button
            type="button"
            @click="isInfoModalOpen = true"
            class="btn bg-white hover:bg-slate-50 text-slate-700 hover:text-indigo-600 inline-flex items-center gap-2 rounded-xl px-3.5 py-2.5 transition-colors duration-150 active:scale-95 border border-slate-200 shadow-sm font-bold text-sm cursor-pointer"
          >
            <i class="fa-solid fa-circle-info text-indigo-500"></i>
            <span>Guide</span>
          </button>

          <!-- Quick Create Dropdown -->
          <div
            class="ms-auto relative"
            v-click-outside="() => (isCreateDropdownOpen = false)"
          >
            <button
              type="button"
              @click="isCreateDropdownOpen = !isCreateDropdownOpen"
              class="text-teal-900 btn bg-teal-100 hover:bg-teal-200 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 transition-colors duration-150 active:scale-95 border border-teal-200 shadow-sm font-bold text-sm cursor-pointer"
            >
              <i class="fa-solid fa-plus text-base text-teal-700"></i>
              <span>{{ $t("util.create") || "Create Promotion" }}</span>
              <i
                class="fa-solid fa-chevron-down text-xs transition-transform duration-200 text-teal-700"
                :class="{ 'rotate-180': isCreateDropdownOpen }"
              ></i>
            </button>

            <!-- Dropdown menu items -->
            <Transition name="fade-slide">
              <div
                v-if="isCreateDropdownOpen"
                class="absolute right-0 top-full mt-2 z-50 w-72 origin-top-right rounded-2xl bg-white border border-slate-200 shadow-2xl p-2 space-y-1 text-left"
              >
                <div
                  class="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100"
                >
                  Choose Promotion Type
                </div>

                <router-link
                  v-for="item in createPromotionTypes"
                  :key="item.title"
                  :to="{
                    name: item.routeName,
                    params: routeParams,
                    query: item.query,
                  }"
                  class="flex items-start gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors group"
                  @click="isCreateDropdownOpen = false"
                >
                  <div
                    :class="[
                      'p-2 w-10 h-10 flex items-center justify-center rounded-lg transition-colors flex-shrink-0',
                      item.iconBg,
                    ]"
                  >
                    <i :class="[item.icon, 'text-sm']"></i>
                  </div>
                  <div>
                    <div class="text-sm font-bold text-slate-800">
                      {{ item.title }}
                    </div>
                    <div class="text-xs text-slate-500">
                      {{ item.description }}
                    </div>
                  </div>
                </router-link>
              </div>
            </Transition>
          </div>
        </div>
      </div>

      <!-- Category Filter Tabs -->
      <div class="mb-4 overflow-x-auto pb-1">
        <div
          class="inline-flex p-1.5 bg-white rounded-2xl border border-slate-200 shadow-sm gap-1"
        >
          <button
            v-for="tab in categoryTabs"
            :key="tab.key"
            @click="setCategoryTab(tab.key)"
            :class="[
              'px-3.5 py-2 text-xs font-bold rounded-xl transition-all duration-200 flex items-center gap-2 whitespace-nowrap',
              activeTab === tab.key
                ? 'bg-slate-900 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/80',
            ]"
          >
            <i :class="tab.icon"></i>
            <span>{{ tab.label }}</span>
            <span
              v-if="tab.count !== undefined"
              :class="[
                'text-[10px] px-2 py-0.5 rounded-full font-extrabold',
                activeTab === tab.key
                  ? 'bg-white/20 text-white'
                  : 'bg-slate-200 text-slate-700',
              ]"
            >
              {{ tab.count }}
            </span>
          </button>
        </div>
      </div>

      <!-- Main Box with Filters and Table -->
      <div class="box content-box-style large ov-hidd p-0 min-h-[70vh]">
        <Filters
          :options="currentFilterOptions"
          :showActionButton="true"
          :search="search"
          :resetFilter="resetFilter"
          :resetPagination="resetPagination"
        />

        <!-- Bulk Actions Bar -->
        <Transition name="fade-slide">
          <div
            v-if="campaignsSelected.length > 0"
            class="manage-freebets-status mt-3 px-4"
          >
            <div class="flex items-center gap-2">
              <button
                v-for="(btn, index) in actionButtons"
                :key="index"
                @click="btn.action()"
                :class="[
                  'inline-flex items-center gap-2 px-4 py-1.5 rounded-full font-bold text-sm transition-all duration-200 active:scale-95 shadow-sm whitespace-nowrap',
                  btn.name === 'Enable'
                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border border-emerald-200'
                    : '',
                  btn.name === 'Disable'
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-200'
                    : '',
                  btn.name === 'Remove'
                    ? 'bg-rose-100 text-rose-700 hover:bg-rose-200 border border-rose-200'
                    : '',
                ]"
              >
                <span>{{ btn.name }}</span>
                <span class="opacity-60 text-xs"
                  >({{ campaignsSelected.length }})</span
                >
                <i :class="`fas fa-${btn.icon} text-xs`"></i>
              </button>
            </div>
          </div>
        </Transition>

        <!-- Loading state -->
        <div
          v-if="isLoading"
          class="flex-grow flex flex-col items-center justify-center p-20"
        >
          <Loader />
        </div>

        <!-- DataTable -->
        <div
          v-else
          class="table-container-custom shadow-sm rounded-2xl overflow-hidden border border-slate-200 my-3 mx-2"
        >
          <DataTable
            v-model:selection="campaignsSelected"
            :value="tableData"
            dataKey="_id"
            :lazy="true"
            :paginator="true"
            :rows="selectedLimit"
            :totalRecords="totalElements"
            :first="(currentPage - 1) * selectedLimit"
            :rowsPerPageOptions="[15, 25, 50, 100]"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            :currentPageReportTemplate="
              isRainView
                ? 'Showing {first} to {last} of {totalRecords} rain campaigns'
                : 'Showing {first} to {last} of {totalRecords} promotions'
            "
            showCurrentPageReport
            @page="onPage"
            @sort="onSort"
            :sortField="sortField"
            :sortOrder="sortOrder"
            removableSort
            stripedRows
            scrollable
            scrollHeight="flex"
            responsiveLayout="stack"
            class="promotions-datatable p-datatable-sm"
          >
            <template #empty>
              <div class="text-center py-10 text-slate-400">
                <i
                  class="fa-solid fa-box-open text-3xl mb-2 text-slate-300"
                ></i>
                <div>No promotions found matching the selected criteria.</div>
              </div>
            </template>

            <Column selectionMode="multiple" headerStyle="width: 2.5rem" />

            <!-- Title & Type Column -->
            <Column
              field="title"
              header="Promotion"
              sortable
              style="min-width: 260px"
            >
              <template #body="{ data }">
                <div class="flex flex-col gap-1 py-1">
                  <span
                    class="font-bold text-slate-800 text-sm hover:text-blue-600 cursor-pointer"
                    @click="goToDetails(data)"
                  >
                    {{ data.title }}
                  </span>
                  <div class="flex items-center gap-1.5 flex-wrap">
                    <!-- Freebet Badge -->
                    <span
                      v-if="
                        data.promotion_type === 'freebet' ||
                        data.mode === 'freebet'
                      "
                      class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200"
                    >
                      <i class="fa-solid fa-ticket text-[10px]"></i> Freebet
                    </span>

                    <!-- Tournament Badge -->
                    <span
                      v-else-if="data.mode === 'tournament'"
                      class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200"
                    >
                      <i class="fa-solid fa-trophy text-[10px]"></i> Tournament
                    </span>

                    <!-- Race Badge -->
                    <span
                      v-else-if="data.mode === 'race'"
                      class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200"
                    >
                      <i class="fa-solid fa-flag-checkered text-[10px]"></i>
                      Race
                    </span>

                    <!-- Mission Badge -->
                    <span
                      v-else-if="data.mode === 'mission'"
                      class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-cyan-50 text-cyan-700 border border-cyan-200"
                    >
                      <i class="fa-solid fa-bullseye text-[10px]"></i> Mission
                    </span>

                    <!-- Chat Rain Badge -->
                    <span
                      v-else-if="data.mode === 'rain' && !data.is_mistery"
                      class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-sky-50 text-sky-700 border border-sky-200"
                    >
                      <i
                        class="fa-solid fa-cloud-showers-heavy text-[10px]"
                      ></i>
                      Chat Rain
                    </span>

                    <!-- Mystery Bet Drop Badge -->
                    <span
                      v-else-if="data.mode === 'rain' && data.is_mistery"
                      class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-purple-50 text-purple-700 border border-purple-200"
                    >
                      <i class="fa-solid fa-gift text-[10px]"></i> Mystery Bet
                      Drop
                    </span>

                    <!-- Fallback Badge -->
                    <span
                      v-else
                      class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-700 border border-slate-200"
                    >
                      <i class="fa-solid fa-tag text-[10px]"></i>
                      {{ data.mode || "Promotion" }}
                    </span>

                    <span class="text-[10px] text-slate-400 font-mono"
                      >ID: {{ data.external_id || data._id }}</span
                    >
                  </div>
                </div>
              </template>
            </Column>

            <!-- Skin Column -->
            <Column field="skin_id" header="Skin" sortable style="width: 6rem">
              <template #body="{ data }">
                <span
                  class="px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-xs font-mono text-slate-700 font-semibold"
                >
                  {{ data.skin_id ?? "All" }}
                </span>
              </template>
            </Column>

            <!-- Status Column -->
            <Column
              field="is_enabled"
              header="Status"
              sortable
              style="width: 8rem"
            >
              <template #body="{ data }">
                <span
                  :class="[
                    'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold',
                    data.is_enabled
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-rose-50 text-rose-700 border border-rose-200',
                  ]"
                >
                  <i
                    :class="`fas fa-${
                      data.is_enabled ? 'check-circle' : 'times-circle'
                    }`"
                  ></i>
                  {{ data.is_enabled ? "Enabled" : "Disabled" }}
                </span>
              </template>
            </Column>

            <!-- Rain Mode: Total Rain Column -->
            <Column
              v-if="isRainView"
              field="total_rain"
              header="Total Rain"
              sortable
              style="min-width: 120px"
            >
              <template #body="{ data }">
                <div class="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                  <i
                    :class="data.is_mistery ? 'fa-solid fa-gift text-purple-500' : 'fa-solid fa-cloud-showers-heavy text-sky-500'"
                    class="text-[11px]"
                  ></i>
                  <span>{{ data.total_rain ?? 0 }}</span>
                  <span class="text-slate-400 font-normal text-[10px]">
                    {{ data.is_mistery ? 'drops' : 'rains' }}
                  </span>
                </div>
              </template>
            </Column>

            <!-- Schedule Column -->
            <Column header="Schedule" style="min-width: 180px">
              <template #body="{ data }">
                <div class="text-xs space-y-0.5">
                  <div class="text-slate-700 flex items-center gap-1.5">
                    <i
                      class="far fa-calendar-alt text-slate-400 text-[10px]"
                    ></i>
                    <span>{{ formatDate(data.start_date) }}</span>
                    <span class="text-slate-400">→</span>
                    <span>{{ formatDate(data.end_date) }}</span>
                  </div>
                  <div class="text-slate-500 flex items-center gap-1.5">
                    <i class="far fa-clock text-slate-400 text-[10px]"></i>
                    <span
                      >{{ formatTime(data.start_time) }} -
                      {{ formatTime(data.end_time) }}</span
                    >
                  </div>
                </div>
              </template>
            </Column>

            <!-- Rewards / Freebets summary Column -->
            <Column v-if="!isRainView" header="Reward Pool" style="min-width: 140px">
              <template #body="{ data }">
                <div class="text-xs">
                  <div
                    v-if="data.mode === 'rain'"
                    class="flex items-center gap-1 text-slate-700 font-semibold"
                  >
                    <i class="fa-solid fa-droplet text-sky-500 text-[11px]"></i>
                    <span
                      >{{ data.total_rain || data.count_day_freebet || 0 }} Rain
                      Drops</span
                    >
                  </div>
                  <div
                    v-else-if="
                      data.mode === 'freebet' ||
                      data.promotion_type === 'freebet'
                    "
                    class="flex items-center gap-1 text-emerald-700 font-semibold"
                  >
                    <i
                      class="fa-solid fa-ticket text-emerald-500 text-[11px]"
                    ></i>
                    <span>{{ data.quantity || 1 }} Freebets</span>
                    <span
                      v-if="data.currencies && data.currencies.length"
                      class="text-slate-400 text-[10px] font-normal"
                    >
                      ({{ data.currencies.length }} currs)
                    </span>
                  </div>
                  <div
                    v-else-if="
                      ['tournament', 'race', 'mission'].includes(data.mode)
                    "
                    class="flex items-center gap-1 text-amber-700 font-semibold"
                  >
                    <i
                      class="fa-solid fa-trophy text-amber-500 text-[11px]"
                    ></i>
                    <span>
                      {{
                        data.prizes
                          ? (Array.isArray(data.prizes)
                              ? data.prizes.length
                              : Object.keys(data.prizes).length) + " Prizes"
                          : "Prizes Set"
                      }}
                    </span>
                    <span
                      v-if="data.currency"
                      class="text-slate-400 text-[10px] font-normal"
                    >
                      ({{ data.currency }})
                    </span>
                  </div>
                  <div v-else class="text-slate-600">
                    {{ data.amount ? `${data.amount} Freebets` : "Configured" }}
                  </div>
                </div>
              </template>
            </Column>

            <!-- Actions Column -->
            <Column
              header="Actions"
              :exportable="false"
              style="min-width: 130px"
            >
              <template #body="{ data }">
                <div class="flex items-center gap-1">
                  <!-- View Details -->
                  <Button
                    icon="pi pi-eye"
                    rounded
                    text
                    severity="info"
                    v-tooltip.top="'View Details'"
                    @click="goToDetails(data)"
                    class="hover:bg-blue-50 text-blue-600 rounded-full w-8 h-8 p-0"
                  />

                  <!-- Edit -->
                  <Button
                    icon="pi pi-pencil"
                    rounded
                    text
                    severity="warning"
                    v-tooltip.top="'Edit Promotion'"
                    @click="goToEdit(data)"
                    class="hover:bg-amber-50 text-amber-600 rounded-full w-8 h-8 p-0"
                  />

                  <!-- Delete -->
                  <Button
                    icon="pi pi-trash"
                    rounded
                    text
                    severity="danger"
                    v-tooltip.top="'Delete Promotion'"
                    @click="removePromotionRow(data)"
                    class="hover:bg-rose-50 text-rose-600 rounded-full w-8 h-8 p-0"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>

    <!-- Info Modal: Campaign Types & Promotions Guide -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isInfoModalOpen"
          class="fixed inset-0 z-[923121329999] flex items-center justify-center p-4 bg-slate-900/50"
          @click.self="isInfoModalOpen = false"
        >
          <div
            class="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-200 text-slate-800"
            @click.stop
          >
            <!-- Modal Header -->
            <div
              class="px-4 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50"
            >
              <div class="flex items-center gap-3">
                <div
                  class="w-10 h-10 rounded-2xl bg-indigo-100 text-indigo-600 flex items-center justify-center text-lg shadow-sm"
                >
                  <i class="fa-solid fa-book-open"></i>
                </div>
                <div>
                  <h3 class="text-lg font-bold text-slate-900">
                    Campaign Types & Engagement Guide
                  </h3>
                  <p class="text-xs text-slate-500">
                    Overview of all promotional mechanics supported across the
                    platform
                  </p>
                </div>
              </div>
              <button
                @click="isInfoModalOpen = false"
                class="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800 flex items-center justify-center transition-colors cursor-pointer"
                type="button"
                aria-label="Close modal"
              >
                <i class="fa-solid fa-xmark text-sm"></i>
              </button>
            </div>

            <!-- Modal Body (Cards Grid) -->
            <div
              class="p-6 overflow-y-auto space-y-4 max-h-[calc(90vh-140px)] custom-scrollbar"
            >
              <!-- Tournament -->
              <div
                class="p-4 rounded-2xl border border-amber-200/80 bg-amber-50/30 hover:bg-amber-50/60 transition-colors"
              >
                <div
                  class="flex items-start justify-between gap-3 mb-1.5 flex-wrap"
                >
                  <div class="flex items-center gap-2.5">
                    <div
                      class="w-8 h-8 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center text-sm"
                    >
                      <i class="fa-solid fa-trophy"></i>
                    </div>
                    <div>
                      <h4 class="text-sm font-bold text-slate-900">
                        Tournaments
                      </h4>
                      <span class="text-[10px] font-mono text-slate-400"
                        >Mode: tournament</span
                      >
                    </div>
                  </div>
                  <span
                    class="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200"
                  >
                    Leaderboard Rankings
                  </span>
                </div>
                <p class="text-xs text-slate-600 leading-relaxed mt-2">
                  Competitive leaderboard campaigns where players accumulate
                  ranking points by placing qualifying wagers, winning
                  consecutive rounds, or hitting high multipliers. When the
                  tournament ends, top-ranking players on the leaderboard
                  automatically receive their share of the configured prize
                  pool.
                </p>
              </div>

              <!-- Race -->
              <div
                class="p-4 rounded-2xl border border-indigo-200/80 bg-indigo-50/30 hover:bg-indigo-50/60 transition-colors"
              >
                <div
                  class="flex items-start justify-between gap-3 mb-1.5 flex-wrap"
                >
                  <div class="flex items-center gap-2.5">
                    <div
                      class="w-8 h-8 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm"
                    >
                      <i class="fa-solid fa-flag-checkered"></i>
                    </div>
                    <div>
                      <h4 class="text-sm font-bold text-slate-900">Races</h4>
                      <span class="text-[10px] font-mono text-slate-400"
                        >Mode: race</span
                      >
                    </div>
                  </div>
                  <span
                    class="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 text-indigo-800 border border-indigo-200"
                  >
                    Sprint Competition
                  </span>
                </div>
                <p class="text-xs text-slate-600 leading-relaxed mt-2">
                  High-speed sprint competitions with designated target
                  milestones. Players race against each other within a limited
                  timeframe to achieve target turnover or point milestones.
                  Rewards are granted promptly as milestones are completed.
                </p>
              </div>

              <!-- Mission -->
              <div
                class="p-4 rounded-2xl border border-cyan-200/80 bg-cyan-50/30 hover:bg-cyan-50/60 transition-colors"
              >
                <div
                  class="flex items-start justify-between gap-3 mb-1.5 flex-wrap"
                >
                  <div class="flex items-center gap-2.5">
                    <div
                      class="w-8 h-8 rounded-xl bg-cyan-100 text-cyan-600 flex items-center justify-center text-sm"
                    >
                      <i class="fa-solid fa-bullseye"></i>
                    </div>
                    <div>
                      <h4 class="text-sm font-bold text-slate-900">Missions</h4>
                      <span class="text-[10px] font-mono text-slate-400"
                        >Mode: mission</span
                      >
                    </div>
                  </div>
                  <span
                    class="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-cyan-100 text-cyan-800 border border-cyan-200"
                  >
                    Quests & Goals
                  </span>
                </div>
                <p class="text-xs text-slate-600 leading-relaxed mt-2">
                  Individual objective-based quests (e.g., play 30 rounds, wager
                  a total of 50€, win 10 rounds). Players complete tasks at
                  their own pace during the campaign timeline and immediately
                  unlock their reward upon completing all required task
                  objectives.
                </p>
              </div>

              <!-- Chat Rain -->
              <div
                class="p-4 rounded-2xl border border-sky-200/80 bg-sky-50/30 hover:bg-sky-50/60 transition-colors"
              >
                <div
                  class="flex items-start justify-between gap-3 mb-1.5 flex-wrap"
                >
                  <div class="flex items-center gap-2.5">
                    <div
                      class="w-8 h-8 rounded-xl bg-sky-100 text-sky-600 flex items-center justify-center text-sm"
                    >
                      <i class="fa-solid fa-cloud-showers-heavy"></i>
                    </div>
                    <div>
                      <h4 class="text-sm font-bold text-slate-900">
                        Chat Rain
                      </h4>
                      <span class="text-[10px] font-mono text-slate-400"
                        >Mode: rain (chat)</span
                      >
                    </div>
                  </div>
                  <span
                    class="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-sky-100 text-sky-800 border border-sky-200"
                  >
                    Community Chat Drops
                  </span>
                </div>
                <p class="text-xs text-slate-600 leading-relaxed mt-2">
                  Automated or manual reward rain bursts dropped directly into
                  the community chat room. Active chat players can click the
                  "Claim" button during the active drop countdown to grab a
                  share of the rain's freebet prize pool.
                </p>
              </div>

              <!-- Mystery Bet Drop -->
              <div
                class="p-4 rounded-2xl border border-purple-200/80 bg-purple-50/30 hover:bg-purple-50/60 transition-colors"
              >
                <div
                  class="flex items-start justify-between gap-3 mb-1.5 flex-wrap"
                >
                  <div class="flex items-center gap-2.5">
                    <div
                      class="w-8 h-8 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center text-sm"
                    >
                      <i class="fa-solid fa-gift"></i>
                    </div>
                    <div>
                      <h4 class="text-sm font-bold text-slate-900">
                        Mystery Bet Drop
                      </h4>
                      <span class="text-[10px] font-mono text-slate-400"
                        >Mode: rain (is_mistery: true)</span
                      >
                    </div>
                  </div>
                  <span
                    class="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-800 border border-purple-200"
                  >
                    Live Random Drops
                  </span>
                </div>
                <p class="text-xs text-slate-600 leading-relaxed mt-2">
                  Random surprise rewards awarded live to players during active
                  gameplay immediately after a qualifying bet is placed. Players
                  do not need to be active in chat to receive mystery drops;
                  awards are delivered instantly to eligible gaming sessions.
                </p>
              </div>

              <!-- Standalone Freebets Note -->
              <div
                class="p-4 rounded-2xl border border-emerald-200/80 bg-emerald-50/30 hover:bg-emerald-50/60 transition-colors"
              >
                <div
                  class="flex items-start justify-between gap-3 mb-1.5 flex-wrap"
                >
                  <div class="flex items-center gap-2.5">
                    <div
                      class="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center text-sm"
                    >
                      <i class="fa-solid fa-ticket"></i>
                    </div>
                    <div>
                      <h4 class="text-sm font-bold text-slate-900">
                        Standalone Freebets (Direct Page)
                      </h4>
                      <span class="text-[10px] font-mono text-slate-400"
                        >Endpoint: /freebets</span
                      >
                    </div>
                  </div>
                  <span
                    class="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200"
                  >
                    Direct Allocations
                  </span>
                </div>
                <p class="text-xs text-slate-600 leading-relaxed mt-2">
                  Direct player allocations created with explicit currency
                  amounts and quotas, managed in the separate
                  <router-link
                    :to="{ name: 'Freebets' }"
                    @click="isInfoModalOpen = false"
                    class="text-emerald-700 font-bold underline"
                    >Freebets page</router-link
                  >. Freebets won or claimed from Tournaments, Races, Missions,
                  or Chat Rains are also tracked there with their campaign
                  origin tags.
                </p>
              </div>
            </div>

            <!-- Modal Footer -->
            <div
              class="px-6 py-3.5 border-t border-slate-100 bg-slate-50 flex items-center justify-end"
            >
              <button
                type="button"
                @click="isInfoModalOpen = false"
                class="px-5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs transition-colors shadow-sm active:scale-95 cursor-pointer"
              >
                Close Guide
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import { getFetch, putFetch, deleteRequest } from "@/api/axiosClient";
import { mapGetters } from "vuex";
import { formatString } from "@/helpers/date";
import Swal from "sweetalert2";

import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { Button } from "primevue";

import Loader from "../components/Loader.vue";
import Filters from "../components/table/Filters.vue";
import ProgressModal from "../components/Modal/ProgressModal.vue";

export default {
  name: "PromotionsHub",
  components: {
    DataTable,
    Column,
    Button,
    Loader,
    Filters,
    ProgressModal,
  },
  directives: {
    "click-outside": {
      mounted(el, binding) {
        el.clickOutsideEvent = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.body.addEventListener("click", el.clickOutsideEvent);
      },
      unmounted(el) {
        document.body.removeEventListener("click", el.clickOutsideEvent);
      },
    },
  },
  data() {
    return {
      isLoading: false,
      isLoadingAction: false,
      isCreateDropdownOpen: false,
      createPromotionTypes: [
        {
          title: "Tournament",
          description: "Leaderboard competition & prizes",
          icon: "fa-solid fa-trophy",
          iconBg:
            "bg-amber-100 text-amber-600 group-hover:bg-amber-600 group-hover:text-white",
          routeName: "ChallengesCreate",
          query: { mode: "tournament" },
        },
        {
          title: "Race",
          description: "Fast sprint wager races with target goals",
          icon: "fa-solid fa-flag-checkered",
          iconBg:
            "bg-indigo-100 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white",
          routeName: "ChallengesCreate",
          query: { mode: "race" },
        },
        {
          title: "Mission",
          description: "Player milestone tasks & quest challenges",
          icon: "fa-solid fa-bullseye",
          iconBg:
            "bg-cyan-100 text-cyan-600 group-hover:bg-cyan-600 group-hover:text-white",
          routeName: "ChallengesCreate",
          query: { mode: "mission" },
        },
        {
          title: "Chat Rain",
          description: "Claim-based community drops in chat",
          icon: "fa-solid fa-cloud-showers-heavy",
          iconBg:
            "bg-sky-100 text-sky-600 group-hover:bg-sky-600 group-hover:text-white",
          routeName: "RainsCreate",
          query: { is_mistery: "false" },
        },
        {
          title: "Mystery Bet Drop",
          description: "Random live reward awarded after bets",
          icon: "fa-solid fa-gift",
          iconBg:
            "bg-purple-100 text-purple-600 group-hover:bg-purple-600 group-hover:text-white",
          routeName: "RainsCreate",
          query: { is_mistery: "true" },
        },
        {
          title: "Freebet Campaign",
          description: "Standalone direct player freebets",
          icon: "fa-solid fa-ticket",
          iconBg:
            "bg-emerald-100 text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white",
          routeName: "FreebetsCreate",
        },
      ],
      isInfoModalOpen: false,
      progressIndicator: "",
      processedItems: 0,
      campaignsSelected: [],

      activeTab: "all",
      categoryTabs: [
        { key: "all", label: "All Campaigns", icon: "fa-solid fa-border-all" },
        { key: "tournament", label: "Tournaments", icon: "fa-solid fa-trophy" },
        { key: "race", label: "Races", icon: "fa-solid fa-flag-checkered" },
        { key: "mission", label: "Missions", icon: "fa-solid fa-bullseye" },
        {
          key: "chat_rain",
          label: "Chat Rain",
          icon: "fa-solid fa-cloud-showers-heavy",
        },
        {
          key: "mystery_drop",
          label: "Mystery Bet Drop",
          icon: "fa-solid fa-gift",
        },
      ],

      tableData: [],
      currentPage: 1,
      selectedLimit: 25,
      totalElements: 0,
      totalPages: 1,
      sortField: "start_date",
      sortOrder: -1,

      filter_id: "",
      filter_title: "",
      filter_start_date: null,
      filter_end_date: null,
      filter_has_rain: "All",
      filter_is_enabled: "All",
      filter_skin_id: "All",

      filterOptions: [
        {
          label: "ID",
          type: "text",
          property: "filter_id",
          placeholder: "Search ID",
        },
        {
          label: "Title",
          type: "text",
          property: "filter_title",
          placeholder: "Search Title",
        },
        {
          label: "Start Date",
          type: "datetime-local",
          property: "filter_start_date",
          isDateTime: true,
        },
        {
          label: "End Date",
          type: "datetime-local",
          property: "filter_end_date",
          isDateTime: true,
        },
        {
          label: "Status",
          type: "select",
          property: "filter_is_enabled",
          options: ["All", "True", "False"],
        },
      ],

      actionButtons: [
        {
          name: "Enable",
          icon: "circle-check",
          action: () => this.bulkChangeStatus(true),
        },
        {
          name: "Disable",
          icon: "circle-xmark",
          action: () => this.bulkChangeStatus(false),
        },
        {
          name: "Remove",
          icon: "trash",
          action: () => this.bulkDelete(),
        },
      ],
    };
  },
  computed: {
    ...mapGetters(["isMenuOpen", "skinStorage", "isAdmin", "allowedSkins"]),
    isRainView() {
      return this.activeTab === "chat_rain" || this.activeTab === "mystery_drop";
    },
    currentFilterOptions() {
      const skins =
        this.isAdmin && Array.isArray(this.allowedSkins)
          ? ["All", ...this.allowedSkins.map((s) => s.ref_id)]
          : ["All"];

      const base = [
        { label: "ID", type: "text", property: "filter_id", placeholder: "Search ID" },
        { label: "Title", type: "text", property: "filter_title", placeholder: "Search Title" },
        {
          label: "Start Date",
          type: "datetime-local",
          property: "filter_start_date",
          isDateTime: true,
        },
        {
          label: "End Date",
          type: "datetime-local",
          property: "filter_end_date",
          isDateTime: true,
        },
      ];

      if (this.isRainView) {
        base.push({
          label: "Has Rain",
          type: "select",
          property: "filter_has_rain",
          options: ["All", "True", "False"],
        });
      }

      base.push({
        label: "Status",
        type: "select",
        property: "filter_is_enabled",
        options: ["All", "True", "False"],
      });

      if (this.isAdmin && skins.length > 1) {
        base.push({
          label: "Skin ID",
          type: "select",
          property: "filter_skin_id",
          options: skins,
        });
      }

      return base;
    },
    routeParams() {
      return {
        lang: this.$route.params.lang || "en",
        ...(this.$route.params.skinFilt || this.skinStorage
          ? {
              skinFilt: (
                this.$route.params.skinFilt || this.skinStorage
              ).toString(),
            }
          : {}),
      };
    },
  },
  async mounted() {
    await this.search();
  },
  methods: {
    setCategoryTab(tabKey) {
      this.activeTab = tabKey;
      this.currentPage = 1;
      this.campaignsSelected = [];
      this.search();
    },

    async search() {
      this.isLoading = true;
      const params = {
        page: this.currentPage,
        limit: this.selectedLimit,
        sortField: this.sortField,
        sortOrder: this.sortOrder === -1 ? "desc" : "asc",
      };

      if (this.filter_id && this.filter_id.trim()) {
        params.id = this.filter_id.trim();
      }
      if (this.filter_start_date) {
        params.start_date = new Date(this.filter_start_date + "Z");
      }
      if (this.filter_end_date) {
        params.end_date = new Date(this.filter_end_date + "Z");
      }
      if (this.filter_is_enabled !== "All") {
        params.is_enabled = this.filter_is_enabled === "True";
      }
      if (this.isAdmin && this.filter_skin_id !== "All") {
        params.skin_id = parseInt(this.filter_skin_id, 10);
      }

      if (this.isRainView && this.filter_has_rain !== "All") {
        params.has_rain = this.filter_has_rain === "True";
      }

      if (this.activeTab === "chat_rain") {
        params.mode = "rain";
        params.is_mistery = false;
      } else if (this.activeTab === "mystery_drop") {
        params.mode = "rain";
        params.is_mistery = true;
      } else if (["tournament", "race", "mission"].includes(this.activeTab)) {
        params.mode = this.activeTab;
      }

      try {
        const response = await getFetch(
          "/campaigns",
          params,
          this.filter_skin_id === "All",
        );
        let items = (response.data || []).map((c) => ({
          ...c,
          promotion_type:
            c.mode === "rain"
              ? c.is_mistery
                ? "mystery_drop"
                : "chat_rain"
              : c.mode || "campaign",
        }));

        if (this.filter_title && this.filter_title.trim()) {
          const q = this.filter_title.trim().toLowerCase();
          items = items.filter(
            (c) => c.title && c.title.toLowerCase().includes(q),
          );
        }

        this.tableData = items;
        this.totalElements = response.totalItems || items.length;
        this.totalPages = response.totalPages || 1;
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        this.tableData = [];
      } finally {
        this.isLoading = false;
      }
    },

    resetFilter() {
      this.filter_id = "";
      this.filter_title = "";
      this.filter_start_date = null;
      this.filter_end_date = null;
      this.filter_has_rain = "All";
      this.filter_is_enabled = "All";
      this.filter_skin_id = "All";
      this.search();
    },

    resetPagination() {
      this.currentPage = 1;
    },

    onPage(event) {
      this.currentPage = event.page + 1;
      this.selectedLimit = event.rows;
      this.search();
    },

    onSort(event) {
      this.sortField = event.sortField;
      this.sortOrder = event.sortOrder;
      this.search();
    },

    formatDate(date) {
      if (!date) return "-";
      return formatString(date, "YYYY-MM-DD");
    },

    formatTime(time) {
      if (!time && time !== 0) return "--:--";
      if (typeof time === "number") {
        return `${String(time).padStart(2, "0")}:00`;
      }
      return String(time);
    },

    goToDetails(campaign) {
      const skin = campaign.skin_id || this.skinStorage || "";
      if (["tournament", "race", "mission"].includes(campaign.mode)) {
        this.$router.push({
          name: "Challenges",
          params: {
            id: campaign._id,
            lang: this.$route.params.lang || "en",
            skinFilt: skin.toString(),
          },
        });
      } else {
        this.$router.push({
          name: "Rains",
          params: {
            id: campaign._id,
            lang: this.$route.params.lang || "en",
            skinFilt: skin.toString(),
          },
        });
      }
    },

    goToEdit(campaign) {
      const skin = campaign.skin_id || this.skinStorage || "";
      if (["tournament", "race", "mission"].includes(campaign.mode)) {
        this.$router.push({
          name: "ChallengesEdit",
          params: {
            id: campaign._id,
            lang: this.$route.params.lang || "en",
            skinFilt: skin.toString(),
          },
        });
      } else {
        this.$router.push({
          name: "RainsEdit",
          params: {
            id: campaign._id,
            lang: this.$route.params.lang || "en",
            skinFilt: skin.toString(),
          },
        });
      }
    },

    async bulkChangeStatus(enable) {
      this.isLoadingAction = true;
      this.processedItems = 0;
      this.progressIndicator = `${
        enable ? "Enabling" : "Disabling"
      } promotions...`;

      try {
        for (const item of this.campaignsSelected) {
          await putFetch(`/campaigns/${item._id}`, { is_enabled: enable });
          this.processedItems++;
        }
        Swal.fire(
          "Success",
          `Promotions ${enable ? "enabled" : "disabled"} successfully`,
          "success",
        );
        this.campaignsSelected = [];
        this.search();
      } catch (error) {
        Swal.fire("Error", "Error updating promotion status", "error");
      } finally {
        this.isLoadingAction = false;
      }
    },

    async bulkDelete() {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: `Do you want to delete ${this.campaignsSelected.length} selected promotion(s)?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        confirmButtonText: "Yes, delete them",
      });

      if (!result.isConfirmed) return;

      this.isLoadingAction = true;
      this.processedItems = 0;
      this.progressIndicator = "Deleting promotions...";

      try {
        for (const item of this.campaignsSelected) {
          await deleteRequest(`/campaigns/${item._id}`);
          this.processedItems++;
        }
        Swal.fire("Deleted!", "Promotions deleted successfully.", "success");
        this.campaignsSelected = [];
        this.search();
      } catch (error) {
        Swal.fire("Error", "Error deleting promotions", "error");
      } finally {
        this.isLoadingAction = false;
      }
    },

    async removePromotionRow(campaign) {
      const result = await Swal.fire({
        title: "Delete Promotion?",
        text: `Are you sure you want to delete "${campaign.title}"?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        confirmButtonText: "Yes, delete",
      });

      if (!result.isConfirmed) return;

      try {
        await deleteRequest(`/campaigns/${campaign._id}`);
        Swal.fire("Deleted!", "Promotion has been deleted.", "success");
        this.search();
      } catch (error) {
        Swal.fire("Error", "Error deleting promotion", "error");
      }
    },
  },
};
</script>

<style scoped></style>
